import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Package, Loader, AlertCircle, RefreshCw } from 'lucide-react';
import { pOrdersService, IncompleteReceiptLine } from '../../../../services/pOrdersService';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface IncompleteReceiptReportProps {
  onBack?: () => void;
}

/**
 * Punkt 17 (Norlux): Rapport over ufullstendige mottak — ordrelinjer der bestilt antall er større
 * enn ankomst-registrert antall (StcTrans LineStatus >= 20 = ankommet + mottatt). Read-only.
 */
export const IncompleteReceiptReport: React.FC<IncompleteReceiptReportProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const [lines, setLines] = useState<IncompleteReceiptLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [supplierNo, setSupplierNo] = useState('');
  const [warehouse, setWarehouse] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pOrdersService.getIncompleteReceipts({
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        supplierNo: supplierNo || undefined,
        warehouse: warehouse || undefined,
      });
      setLines(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('warehouse.modules.receipt.reports.loadFailed', {}, 'Kunne ikke laste rapporten'));
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, supplierNo, warehouse, t]);

  // Last ved oppstart (filtre kjøres via "Søk"-knappen).
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fmtQty = (n: number) => (Math.round((n || 0) * 1000) / 1000).toString();
  const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString('nb-NO') : '');

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-3 sm:px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 text-gray-500 hover:text-gray-700 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                {t('warehouse.modules.receipt.reports.incompleteReceipts', {}, 'Ufullstendige mottak')}
              </h1>
            </div>
          </div>
          <button
            onClick={load}
            className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1 min-h-[44px] px-2"
          >
            <RefreshCw className="w-4 h-4" /> {t('common.buttons.refresh', {}, 'Oppdater')}
          </button>
        </div>

        {/* Filtre */}
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="flex flex-col text-xs text-gray-500">
            {t('warehouse.modules.receipt.reports.fromDate', {}, 'Fra dato')}
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              className="mt-0.5 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
          </label>
          <label className="flex flex-col text-xs text-gray-500">
            {t('warehouse.modules.receipt.reports.toDate', {}, 'Til dato')}
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
              className="mt-0.5 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
          </label>
          <label className="flex flex-col text-xs text-gray-500">
            {t('warehouse.modules.receipt.reports.supplier', {}, 'Leverandør')}
            <input type="text" value={supplierNo} onChange={(e) => setSupplierNo(e.target.value)}
              className="mt-0.5 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
          </label>
          <label className="flex flex-col text-xs text-gray-500">
            {t('warehouse.modules.receipt.reports.warehouse', {}, 'Lagersted')}
            <input type="text" value={warehouse} onChange={(e) => setWarehouse(e.target.value)}
              className="mt-0.5 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
          </label>
          <button
            onClick={load}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 min-h-[40px]"
          >
            {t('common.buttons.search', {}, 'Søk')}
          </button>
        </div>

        <div className="mt-2 text-sm text-gray-600">
          <span className="font-medium">{lines.length}</span> {t('warehouse.modules.receipt.reports.lines', {}, 'linjer')}
        </div>
      </div>

      <div className="p-3 sm:p-4">
        {loading ? (
          <div className="flex items-center justify-center h-48"><Loader className="w-8 h-8 text-blue-600 animate-spin" /></div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700 break-words">{error}</p>
          </div>
        ) : lines.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
            <Package className="w-10 h-10 mx-auto mb-3 text-gray-400" />
            <p className="font-medium">{t('warehouse.modules.receipt.reports.noIncompleteReceipts', {}, 'Ingen ufullstendige mottak funnet')}</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                  <th className="px-3 py-2">{t('warehouse.modules.receipt.reports.order', {}, 'Ordre')}</th>
                  <th className="px-3 py-2">{t('warehouse.modules.receipt.reports.line', {}, 'Linje')}</th>
                  <th className="px-3 py-2">{t('warehouse.general.itemNumber', {}, 'Varenr')}</th>
                  <th className="px-3 py-2">{t('common.fields.description', {}, 'Beskrivelse')}</th>
                  <th className="px-3 py-2 text-right">{t('warehouse.modules.receipt.reports.ordered', {}, 'Bestilt')}</th>
                  <th className="px-3 py-2 text-right">{t('warehouse.modules.receipt.reports.registered', {}, 'Ankommet')}</th>
                  <th className="px-3 py-2 text-right">{t('warehouse.modules.receipt.reports.remaining', {}, 'Rest')}</th>
                  <th className="px-3 py-2">{t('warehouse.modules.receipt.reports.supplier', {}, 'Leverandør')}</th>
                  <th className="px-3 py-2">{t('warehouse.modules.receipt.reports.orderDate', {}, 'Ordredato')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lines.map((l) => (
                  <tr key={`${l.orderNo}-${l.lineNo}`} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{l.orderNoERP || l.orderNo}</td>
                    <td className="px-3 py-2 text-gray-600">{l.lineNo}</td>
                    <td className="px-3 py-2 text-gray-900 whitespace-nowrap">{l.itemNo}</td>
                    <td className="px-3 py-2 text-gray-600">{l.description1}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{fmtQty(l.orderedQuantity)} {l.itemUnitTxt}</td>
                    <td className="px-3 py-2 text-right">{fmtQty(l.registeredQuantity)}</td>
                    <td className="px-3 py-2 text-right font-medium text-amber-700">{fmtQty(l.remainingQuantity)}</td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{l.supplierName || l.supplierNo}</td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{fmtDate(l.orderDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncompleteReceiptReport;
